import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { AppError, AUTH_ACTION_TYPE, AuthAction, MessageScopeType, MessageService, MessageType } from "@obg/ngx.core";
import { merge, of } from "rxjs";
import { catchError, filter, map, switchMap, tap } from "rxjs/operators";

import { MatNotificationsService } from "../mat-notifications/mat-notifications.service";
import { RealTimeNotificationsAction } from "../real-time-notifications/real-time-notifications.action";
import { RealTimeNotificationsConfig } from "../real-time-notifications/real-time-notifications.config";

import { CUSTOMER_INTERACTION_ACTION_TYPE, CustomerInteractionsAction } from "./customer-interactions.action";
import { CustomerInteractionsAlertAcknowldegementType } from "./customer-interactions.model";
import { CustomerInteractionsService } from "./customer-interactions.service";

@Injectable()
export class CustomerInteractionsEffects {
	fetchUnacknowledgedMessages$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionStart),
			filter(() => this.realTimeNotificationsConfig.getCustomerInteractionsEnabled()),
			map(() => CustomerInteractionsAction.getUnacknowledgedMessages())
		)
	);

	getUnacknowledgedMessages$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CustomerInteractionsAction.getUnacknowledgedMessages),
			switchMap(() =>
				this.customerInteractionsService.getAllUnacknowledged().pipe(
					map((messages) => CustomerInteractionsAction.getUnacknowledgedMessagesSuccess(messages)),
					catchError((error: AppError) => of(CustomerInteractionsAction.getUnacknowledgedMessagesFail(error)))
				)
			)
		)
	);

	showUnacknowledgedMessages$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(CustomerInteractionsAction.getUnacknowledgedMessagesSuccess),
				tap(({ payload }) => this.customerInteractionsService.showUnacknowledgedMessages(payload))
			),
		{ dispatch: false }
	);

	timeTriggerNotifications$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RealTimeNotificationsAction.connectionSuccess),
			filter(() => this.realTimeNotificationsConfig.getCustomerInteractionsTimeTriggerEnabled()),
			map(() =>
				RealTimeNotificationsAction.subscribeToChannel(
					this.realTimeNotificationsConfig.getCustomerInteractionsTimeTriggerTopic()
				)
			)
		)
	);

	toolsTriggerNotifications$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RealTimeNotificationsAction.connectionSuccess),
			filter(() => this.realTimeNotificationsConfig.getCustomerInteractionsToolsTriggerEnabled()),
			map(() =>
				RealTimeNotificationsAction.subscribeToChannel(
					this.realTimeNotificationsConfig.getCustomerInteractionsToolsTriggerTopic()
				)
			)
		)
	);

	wageringTriggerNotifications$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RealTimeNotificationsAction.connectionSuccess),
			filter(() => this.realTimeNotificationsConfig.getCustomerInteractionsWageringTriggerEnabled()),
			map(() =>
				RealTimeNotificationsAction.subscribeToChannel(
					this.realTimeNotificationsConfig.getCustomerInteractionsWageringTriggerTopic()
				)
			)
		)
	);

	lossTriggerNotifications$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RealTimeNotificationsAction.connectionSuccess),
			filter(() => this.realTimeNotificationsConfig.getCustomerInteractionsLossTriggerEnabled()),
			map(() =>
				RealTimeNotificationsAction.subscribeToChannel(
					this.realTimeNotificationsConfig.getCustomerInteractionsLossTriggerTopic()
				)
			)
		)
	);

	depositTriggerNotifications$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RealTimeNotificationsAction.connectionSuccess),
			filter(() => this.realTimeNotificationsConfig.getCustomerInteractionsDepositTriggerEnabled()),
			map(() =>
				RealTimeNotificationsAction.subscribeToChannel(
					this.realTimeNotificationsConfig.getCustomerInteractionsDepositTriggerTopic()
				)
			)
		)
	);

	openAlert$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(CustomerInteractionsAction.openAlert),
				tap(({ payload }) => this.store.dispatch(CustomerInteractionsAction.markAlertAsShown(payload))),
				switchMap(({ payload }) => {
					switch (payload.Topic) {
						case this.realTimeNotificationsConfig.getCustomerInteractionsDepositTriggerTopic():
							return this.notificationsService.openCustomerInteractionsDepositTriggerAlert$(
								payload.MessageId
							);
						case this.realTimeNotificationsConfig.getCustomerInteractionsLossTriggerTopic():
							return this.notificationsService.openCustomerInteractionsLossTriggerAlert$(
								payload.MessageId
							);
						case this.realTimeNotificationsConfig.getCustomerInteractionsTimeTriggerTopic():
							return this.notificationsService.openCustomerInteractionsTimeTriggerAlert$(
								payload.MessageId
							);
						case this.realTimeNotificationsConfig.getCustomerInteractionsToolsTriggerTopic():
							return this.notificationsService.openCustomerInteractionsToolsTriggerAlert$(
								payload.MessageId
							);
						case this.realTimeNotificationsConfig.getCustomerInteractionsWageringTriggerTopic():
							return this.notificationsService.openCustomerInteractionsWageringTriggerAlert$(
								payload.MessageId
							);
						default:
							return of(undefined);
					}
				})
			),
		{ dispatch: false }
	);

	markAlertAsShown$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CustomerInteractionsAction.markAlertAsShown),
			switchMap(({ payload }) =>
				this.customerInteractionsService.markAsShown(payload).pipe(
					map(() => CustomerInteractionsAction.markAlertAsShownSuccess()),
					catchError((error: AppError) => of(CustomerInteractionsAction.markAlertAsShownFail(error)))
				)
			)
		)
	);

	markAlertAsConfirmed$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CustomerInteractionsAction.markAlertAsConfirmed),
			switchMap(({ payload }) =>
				this.customerInteractionsService
					.markAsAcknowledged(payload, CustomerInteractionsAlertAcknowldegementType.Confirmed)
					.pipe(
						map(() => CustomerInteractionsAction.markAlertAsConfirmedSuccess()),
						catchError((error: AppError) => of(CustomerInteractionsAction.markAlertAsConfirmedFail(error)))
					)
			)
		)
	);

	markAlertAsLoggedOut$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CustomerInteractionsAction.markAlertAsLoggedOut),
			switchMap(({ payload }) =>
				this.customerInteractionsService
					.markAsAcknowledged(payload, CustomerInteractionsAlertAcknowldegementType.LoggedOut)
					.pipe(
						map(() => CustomerInteractionsAction.markAlertAsLoggedOutSuccess()),
						catchError((error: AppError) => of(CustomerInteractionsAction.markAlertAsLoggedOutFail(error)))
					)
			)
		)
	);

	markAlertAsDepositLimit$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CustomerInteractionsAction.markAlertAsDepositLimit),
			switchMap(({ payload }) =>
				this.customerInteractionsService
					.markAsAcknowledged(
						payload,
						CustomerInteractionsAlertAcknowldegementType.ResponsibleGamingDepositLimit
					)
					.pipe(
						map(() => CustomerInteractionsAction.markAlertAsDepositLimitSuccess()),
						catchError((error: AppError) =>
							of(CustomerInteractionsAction.markAlertAsDepositLimitFail(error))
						)
					)
			)
		)
	);

	markAlertAsMoreInfo$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CustomerInteractionsAction.markAlertAsMoreInfo),
			switchMap(({ payload }) =>
				this.customerInteractionsService
					.markAsAcknowledged(
						payload,
						CustomerInteractionsAlertAcknowldegementType.ResponsibleGamingInformation
					)
					.pipe(
						map(() => CustomerInteractionsAction.markAlertAsMoreInfoSuccess()),
						catchError((error: AppError) => of(CustomerInteractionsAction.markAlertAsMoreInfoFail(error)))
					)
			)
		)
	);

	logoutUser$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				CustomerInteractionsAction.markAlertAsLoggedOutSuccess,
				CustomerInteractionsAction.markAlertAsLoggedOutFail
			),
			map(() => this.authAction.logout())
		)
	);

	initMessages$ = createEffect(
		() =>
			merge(
				this.messageService.mapErrorByContext$(
					{
						[CUSTOMER_INTERACTION_ACTION_TYPE.getUnacknowledgedMessagesFail]:
							CUSTOMER_INTERACTION_ACTION_TYPE.getUnacknowledgedMessages,
						[CUSTOMER_INTERACTION_ACTION_TYPE.markAsShownFail]:
							CUSTOMER_INTERACTION_ACTION_TYPE.markAsShown,
						[CUSTOMER_INTERACTION_ACTION_TYPE.markAsConfirmedFail]:
							CUSTOMER_INTERACTION_ACTION_TYPE.markAsConfirmed,
						[CUSTOMER_INTERACTION_ACTION_TYPE.markAsLoggedOutFail]:
							CUSTOMER_INTERACTION_ACTION_TYPE.markAsLoggedOut,
					},
					MessageScopeType.Global,
					MessageType.Error
				),
				this.messageService.mapClearByContext$({
					[CUSTOMER_INTERACTION_ACTION_TYPE.getUnacknowledgedMessages]:
						CUSTOMER_INTERACTION_ACTION_TYPE.getUnacknowledgedMessages,
					[CUSTOMER_INTERACTION_ACTION_TYPE.markAsShown]: CUSTOMER_INTERACTION_ACTION_TYPE.markAsShown,
					[CUSTOMER_INTERACTION_ACTION_TYPE.markAsConfirmed]:
						CUSTOMER_INTERACTION_ACTION_TYPE.markAsConfirmed,
					[CUSTOMER_INTERACTION_ACTION_TYPE.markAsLoggedOut]:
						CUSTOMER_INTERACTION_ACTION_TYPE.markAsLoggedOut,
				})
			),
		{ dispatch: false }
	);

	constructor(
		private store: Store,
		private actions$: Actions,
		private authAction: AuthAction,
		private messageService: MessageService,
		private notificationsService: MatNotificationsService,
		private realTimeNotificationsConfig: RealTimeNotificationsConfig,
		private customerInteractionsService: CustomerInteractionsService
	) {}
}
